import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  PencilIcon,
  TrashIcon,
  TagIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  CurrencyEuroIcon,
  CubeIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline'
import { GET_OPTION_ITEM, GET_OPTION_GROUPS, GET_PRODUCTS } from '../graphql/product-queries'
import { DELETE_OPTION_ITEM, UPDATE_OPTION_ITEM } from '../graphql/mutations'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import OptionItemIngredientsTab from '../components/costing/OptionItemIngredientsTab'

const OptionItemDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'details' | 'ingredients'>('details')
  const { hasPermission } = usePermissions()
  const showCogs = hasPermission(PERMISSIONS.COSTING_MANAGE)
  
  const { data, loading, error } = useQuery(GET_OPTION_ITEM, {
    variables: { id: parseInt(id!) },
    skip: !id
  })

  const { data: optionGroupsData } = useQuery(GET_OPTION_GROUPS)
  const { data: productsData } = useQuery(GET_PRODUCTS)

  const [updateOptionItem, { loading: updatingPlanningRelevant }] = useMutation(UPDATE_OPTION_ITEM, {
    onError: (error) => toast.error(`Failed to update: ${error.message}`),
  })

  const handlePlanningRelevantToggle = async () => {
    await updateOptionItem({
      variables: {
        id: parseInt(id!),
        data: { planning_relevant: !data?.optionItem?.planning_relevant },
      },
    })
  }

  const [deleteOptionItem] = useMutation(DELETE_OPTION_ITEM, {
    onCompleted: () => {
      toast.success('Option item deleted successfully')
      navigate('/option-items')
    },
    onError: (error) => {
      toast.error(`Failed to delete option item: ${error.message}`)
    }
  })

  const handleDelete = async () => {
    if (data?.optionItem && window.confirm(`Are you sure you want to delete "${data.optionItem.label}"? This action cannot be undone.`)) {
      try {
        await deleteOptionItem({
          variables: { id: Number(data.optionItem.id) }
        })
      } catch (error) {
        // Error is handled in the mutation's onError callback
      }
    }
  }

  const formatPrice = (cents: number) => {
    if (cents === 0) return 'No additional charge'
    const sign = cents > 0 ? '+' : ''
    return `${sign}€${(Math.abs(cents) / 100).toFixed(2)}`
  }

  const getGroupName = (groupId: number) => {
    const groups = optionGroupsData?.optionGroups || []
    const group = groups.find((g: any) => g.id === groupId)
    return group?.name || `Group ${groupId}`
  }

  const getProductName = (productId: number | null) => {
    if (!productId) return null
    const products = productsData?.products || []
    const product = products.find((p: any) => p.id === productId)
    return product?.name || `Product ${productId}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading option item</h3>
        <p className="text-gray-500 mb-6">{error.message}</p>
        <Link to="/option-items" className="btn-primary">
          Back to Option Items
        </Link>
      </div>
    )
  }

  if (!data?.optionItem) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❓</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Option item not found</h3>
        <p className="text-gray-500 mb-6">The option item you're looking for doesn't exist.</p>
        <Link to="/option-items" className="btn-primary">
          Back to Option Items
        </Link>
      </div>
    )
  }

  const optionItem = data.optionItem

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/option-items"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{optionItem.label}</h1>
            <p className="mt-1 text-sm text-gray-500">Option Item Details</p>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <Link
            to={`/option-items/${optionItem.id}/edit`}
            className="btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="btn-danger"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <TagIcon className="h-5 w-5 mr-2" />
            Details
          </button>
          {showCogs && (
            <button
              onClick={() => setActiveTab('ingredients')}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'ingredients'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BeakerIcon className="h-5 w-5 mr-2" />
              Ingredients
            </button>
          )}
        </nav>
      </div>

      {/* Ingredients tab */}
      {activeTab === 'ingredients' && showCogs && (
        <OptionItemIngredientsTab
          optionItemId={parseInt(id!)}
          optionItemLabel={optionItem.label}
        />
      )}

      {/* Details tab */}
      {activeTab === 'details' && (
      <><div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center space-x-3">
                <TagIcon className="h-8 w-8 text-gray-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{optionItem.label}</h3>
                  <p className="text-sm text-gray-500">ID: {optionItem.id}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Option Group
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {getGroupName(optionItem.option_group_id)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price Delta
                  </label>
                  <div className="flex items-center space-x-2">
                    <CurrencyEuroIcon className="h-5 w-5 text-gray-400" />
                    <span className={`text-lg font-medium ${optionItem.price_delta_cents > 0 ? 'text-green-600' : optionItem.price_delta_cents < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      {formatPrice(optionItem.price_delta_cents)}
                    </span>
                  </div>
                </div>
              </div>

              {optionItem.product_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Linked Product
                  </label>
                  <div className="flex items-center space-x-2">
                    <CubeIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-900">{getProductName(optionItem.product_id)}</span>
                  </div>
                </div>
              )}

              {optionItem.sort_order && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort Order
                  </label>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    {optionItem.sort_order}
                  </span>
                </div>
              )}

              {optionItem.image_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image
                  </label>
                  <img
                    src={optionItem.image_url}
                    alt={optionItem.label}
                    className="h-32 w-32 object-cover rounded-lg border border-gray-200"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status and Effects */}
        <div className="space-y-6">
          {/* Default Status */}
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Default Status</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-2">
                {optionItem.default_selected ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Default Selection
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    <XCircleIcon className="h-4 w-4 mr-1" />
                    Optional Selection
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {optionItem.default_selected 
                  ? 'This option is selected by default'
                  : 'This option is optional for customers'
                }
              </p>
            </div>
          </div>

          {/* Planning Relevance */}
          {showCogs && (
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Margin Planning</h2>
              </div>
              <div className="p-6 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={optionItem.planning_relevant ?? false}
                    onChange={handlePlanningRelevantToggle}
                    disabled={updatingPlanningRelevant}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Planning relevant</span>
                </label>
                <p className="text-xs text-gray-500">
                  Mark this option choice when it materially affects margin — it has a surcharge or adds/removes an ingredient with meaningful cost. The planning matrix will include it when modelling margin combinations.
                </p>
              </div>
            </div>
          )}

          {/* Effects */}
          {optionItem.effect_json && (
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Effects</h2>
              </div>
              <div className="p-6">
                <pre className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg overflow-x-auto">
                  {JSON.stringify(optionItem.effect_json, null, 2)}
                </pre>
                <p className="mt-2 text-xs text-gray-500">
                  Dietary and allergen information for this option
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Related Actions */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Related Actions</h2>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-3">
            <Link
              to={`/option-groups/${optionItem.option_group_id}`}
              className="btn-secondary"
            >
              View Option Group
            </Link>
            <Link
              to={`/option-items?groupId=${optionItem.option_group_id}`}
              className="btn-secondary"
            >
              View Other Items in Group
            </Link>
            {optionItem.product_id && (
              <Link
                to={`/products/${optionItem.product_id}`}
                className="btn-secondary"
              >
                View Linked Product
              </Link>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  )
}

export default OptionItemDetailPage
